import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Spinner, Badge } from "react-bootstrap";
import RichTextEditor from "../../components/RichTextEditor";

interface MailRecipientsModalProps {
  show?: boolean;
  handleClose?: () => void;
  retreatDetail?: Record<string, any> | null;
  fetchRecipients: (id: any) => Promise<any>;
  initialMessage?: string;
  actionFunction?: (...args: any[]) => void;
  heading?: string;
  cancel?: string;
  okay?: string;
}

function MailRecipientsModal({
  show,
  handleClose,
  retreatDetail,
  fetchRecipients,
  initialMessage = "",
  actionFunction,
  heading = "Send Email to Lead Couples and Steering Committee",
  cancel = "Cancel",
  okay = "SEND",
}: MailRecipientsModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [steeringList, setSteeringList] = useState<any[]>([]);
  const [adminList, setAdminList] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<any>>(new Set());
  const [message, setMessage] = useState<string>(initialMessage);
  const [additionalEmails, setAdditionalEmails] = useState<any[]>([]);
  const [customEmail, setCustomEmail] = useState<string>("");
  const [fetchError, setFetchError] = useState<string>("");

  useEffect(() => {
    if (!show || !retreatDetail?.id) return;

    setLoading(true);
    setFetchError("");
    setMessage(initialMessage);
    setSelectedIds(new Set());
    setAdditionalEmails([]);
    setCustomEmail("");

    fetchRecipients(retreatDetail.id)
      .then((data: any) => {
        const steering = data.steeringCommittee || [];
        const admins = data.admins || [];
        setSteeringList(steering);
        setAdminList(admins);
        setSelectedIds(
          new Set([...steering.map((u: any) => u.id), ...admins.map((u: any) => u.id)]),
        );
      })
      .catch(() =>
        setFetchError("Failed to load recipients. Please try again."),
      )
      .finally(() => setLoading(false));
  }, [show, retreatDetail?.id]);

  useEffect(() => {
    setMessage(initialMessage);
  }, [initialMessage]);

  const allSteeringSelected = useMemo(
    () =>
      steeringList.length > 0 &&
      steeringList.every((u: any) => selectedIds.has(u.id)),
    [steeringList, selectedIds],
  );
  const allAdminsSelected = useMemo(
    () =>
      adminList.length > 0 &&
      adminList.every((u: any) => selectedIds.has(u.id)),
    [adminList, selectedIds],
  );

  const toggle = (id: any) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleGroup = (list: any[], allSelected: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      allSelected
        ? list.forEach((u: any) => next.delete(u.id))
        : list.forEach((u: any) => next.add(u.id));
      return next;
    });

  const handleAddCustomEmail = () => {
    const emailStr = customEmail.trim();
    if (!emailStr) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailStr)) {
      setFetchError("Please enter a valid email address.");
      return;
    }
    if (additionalEmails.includes(emailStr)) {
      setFetchError("Email address already added.");
      return;
    }
    setAdditionalEmails((prev) => [...prev, emailStr]);
    setCustomEmail("");
    setFetchError("");
  };

  const handleSend = () => {
    setFetchError("");
    if (selectedIds.size === 0 && additionalEmails.length === 0) {
      setFetchError(
        "Please select at least one recipient or add an additional email.",
      );
      return;
    }
    const stripped = message
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    if (!stripped) {
      setFetchError("Please enter a message.");
      return;
    }
    actionFunction(Array.from(selectedIds), message, additionalEmails);
  };

  const handleModalClose = () => {
    setSelectedIds(new Set());
    setAdditionalEmails([]);
    setCustomEmail("");
    setMessage("");
    setFetchError("");
    handleClose();
  };

  const RecipientChip = ({ user }) => {
    const selected = selectedIds.has(user.id);
    return (
      <div
        onClick={() => toggle(user.id)}
        title={user.email}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          borderRadius: "20px",
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: 500,
          border: `1.5px solid ${selected ? "#0d6efd" : "#dee2e6"}`,
          backgroundColor: selected ? "#e8f3ff" : "#f8f9fa",
          color: selected ? "#0d6efd" : "#495057",
          transition: "all 0.15s ease",
          userSelect: "none",
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            backgroundColor: selected ? "#0d6efd" : "#adb5bd",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "9px",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {(user.firstName?.[0] || "?").toUpperCase()}
        </span>
        {user.firstName} {user.lastName} ({user.email})
        {selected && <span style={{ fontSize: "10px", opacity: 0.6 }}>✕</span>}
      </div>
    );
  };

  const RecipientSection = ({
    title,
    list,
    allSelected,
    accentColor,
    addLink,
    addText,
  }) => (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: accentColor,
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600, fontSize: "12px", color: "#343a40" }}>
          {title}
        </span>
        <Badge
          style={{
            backgroundColor: accentColor,
            fontSize: "10px",
            fontWeight: 500,
          }}
        >
          {list.length}
        </Badge>
        {addLink && addText && (
          <a
            href={addLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginLeft: "auto",
              fontSize: "11px",
              color: accentColor,
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {addText}
          </a>
        )}
        {list.length > 0 && (
          <button
            onClick={() => toggleGroup(list, allSelected)}
            style={{
              marginLeft: addLink && addText ? "8px" : "auto",
              background: "none",
              border: "none",
              color: accentColor,
              cursor: "pointer",
              fontSize: "11px",
              padding: "2px 6px",
              borderRadius: "4px",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        )}
      </div>
      {list.length === 0 ? (
        <p style={{ color: "#adb5bd", fontSize: "12px", margin: 0 }}>
          No {title.toLowerCase()} found for this retreat.
        </p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {list.map((user) => (
            <RecipientChip key={user.id} user={user} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ borderBottom: "2px solid #f0f0f0", paddingBottom: "12px" }}
      >
        <Modal.Title style={{ fontSize: "16px" }}>{heading}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px 24px" }}>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: "10px",
            padding: "14px 16px",
            marginBottom: "20px",
            border: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <span
              style={{ fontWeight: 700, fontSize: "13px", color: "#212529" }}
            >
              📋 Recipients
            </span>
            {!loading && steeringList.length + adminList.length > 0 && (
              <span style={{ fontSize: "12px", color: "#6c757d" }}>
                <strong style={{ color: "#0d6efd" }}>{selectedIds.size}</strong>{" "}
                of {steeringList.length + adminList.length} selected
                {selectedIds.size > 0 && (
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#dc3545",
                      cursor: "pointer",
                      fontSize: "11px",
                      marginLeft: "8px",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Clear all
                  </button>
                )}
              </span>
            )}
          </div>

          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "16px 0",
                color: "#6c757d",
              }}
            >
              <Spinner animation="border" size="sm" className="me-2" />
              Loading recipients…
            </div>
          ) : (
            <>
              <RecipientSection
                title="Steering Committee"
                list={steeringList}
                allSelected={allSteeringSelected}
                accentColor="#0d6efd"
                addText="Add New Steering Committiee Member"
                addLink="/admin-dashboard"
              />
              <hr style={{ margin: "12px 0", borderColor: "#dee2e6" }} />
              <RecipientSection
                title="Retreat Administrators"
                list={adminList}
                allSelected={allAdminsSelected}
                accentColor="#198754"
                addText="Add New Retreat Administrators"
                addLink={`/retreat-setup/${retreatDetail.id}`}
              />
              <hr style={{ margin: "12px 0", borderColor: "#dee2e6" }} />
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#ffc107",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#343a40",
                    }}
                  >
                    Additional Emails
                  </span>
                  <Badge
                    style={{
                      backgroundColor: "#ffc107",
                      fontSize: "10px",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    {additionalEmails.length}
                  </Badge>
                </div>
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                >
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="Enter email address"
                    style={{
                      width: "400px",
                      height: "35px",
                      padding: "4px 8px",
                      fontSize: "12px",
                      borderRadius: "6px",
                      border: "1px solid #ced4da",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCustomEmail();
                      }
                    }}
                  />
                  <Button
                    className="passwordMail"
                    onClick={handleAddCustomEmail}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 10px",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    +
                  </Button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {additionalEmails.map((email, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 500,
                        border: "1.5px solid #ffc107",
                        backgroundColor: "#fff8e1",
                        color: "#495057",
                      }}
                    >
                      <span
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          backgroundColor: "#adb5bd",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "9px",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        ?
                      </span>
                      <span>{email}</span>
                      <span
                        style={{
                          cursor: "pointer",
                          fontSize: "10px",
                          opacity: 0.6,
                        }}
                        onClick={() => {
                          setAdditionalEmails((prev) =>
                            prev.filter((_, i) => i !== idx),
                          );
                        }}
                      >
                        ✕
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#212529",
              marginBottom: "8px",
            }}
          >
            ✉️ Message
          </div>
          <RichTextEditor
            value={message}
            onChange={(content) => setMessage(content)}
            height={260}
            variant="minimal"
          />
        </div>
        {fetchError && (
          <div
            style={{
              marginTop: "10px",
              color: "#dc3545",
              fontSize: "13px",
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: "6px",
              padding: "8px 12px",
            }}
          >
            ⚠️ {fetchError}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: "2px solid #f0f0f0" }}>
        <Button variant="secondary" onClick={handleModalClose}>
          {cancel}
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={loading}>
          {okay}
          {selectedIds.size > 0 && (
            <Badge
              bg="light"
              text="dark"
              className="ms-2"
              style={{ fontSize: "11px" }}
            >
              {selectedIds.size}
            </Badge>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MailRecipientsModal;
